/** @jsxImportSource react */
// Shared section primitives. Every marketing page composes from these so the
// brand stays consistent and we don't redefine spacing/typography in 12 places.

import type { ReactNode } from 'react';

interface SectionProps {
  children: ReactNode;
  className?: string;
  /** Anchor target, e.g. for on-page scroll links (`href="#book-call"`). */
  id?: string;
  /** "ink" sections (dark background, light text) need yellow eyebrows / accents */
  tone?: 'light' | 'mist' | 'ink';
  /** Vertical padding scale. "lg" matches our marquee sections (Journey, Final CTA). */
  size?: 'sm' | 'md' | 'lg';
}

const TONE_BG: Record<NonNullable<SectionProps['tone']>, string> = {
  ink: 'bg-[color:var(--color-ink)] text-white',
  light: 'bg-white',
  mist: 'bg-[color:var(--color-bg-mist)]',
};

const SIZE_PY: Record<NonNullable<SectionProps['size']>, string> = {
  lg: 'py-20 sm:py-28 lg:py-[180px]',
  md: 'py-20 sm:py-28 lg:py-[140px]',
  sm: 'py-14 sm:py-20 lg:py-[100px]',
};

export function Section({
  children,
  className = '',
  id,
  size = 'md',
  tone = 'light',
}: SectionProps) {
  return (
    <section
      className={`${TONE_BG[tone]} ${SIZE_PY[size]} ${className}`}
      id={id}
    >
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-20">
        {children}
      </div>
    </section>
  );
}

interface EyebrowProps {
  children: ReactNode;
  tone?: 'amethyst' | 'highlight' | 'ink';
  className?: string;
}

const EYEBROW_COLOR: Record<NonNullable<EyebrowProps['tone']>, string> = {
  amethyst: 'text-[color:var(--color-amethyst)]',
  highlight: 'text-[color:var(--color-highlight)]',
  ink: 'text-[color:var(--color-ink)]',
};

export function Eyebrow({
  children,
  className = '',
  tone = 'amethyst',
}: EyebrowProps) {
  const color = EYEBROW_COLOR[tone];

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div className={`h-px w-8 ${color.replace('text-', 'bg-')}`} />
      <span
        className={`text-[13px] font-medium uppercase tracking-[0.12em] ${color}`}
      >
        {children}
      </span>
    </div>
  );
}

interface HeadingProps {
  children: ReactNode;
  className?: string;
  /** Scale ramp. "xl" is hero-level, "lg" is section-marquee, "md" is body H2. */
  size?: 'xl' | 'lg' | 'md' | 'sm';
  as?: 'h1' | 'h2' | 'h3';
}

const HEADING_SIZE: Record<NonNullable<HeadingProps['size']>, string> = {
  lg: 'text-[clamp(40px,6vw,72px)]',
  md: 'text-[clamp(28px,4vw,48px)]',
  sm: 'text-[clamp(22px,3vw,32px)]',
  xl: 'text-[clamp(40px,8vw,96px)]',
};

export function Heading({
  as: As = 'h2',
  children,
  className = '',
  size = 'lg',
}: HeadingProps) {
  return (
    <As
      className={`font-bold tracking-[-0.032em] [line-height:1.05] ${HEADING_SIZE[size]} ${className}`}
    >
      {children}
    </As>
  );
}

export function Subheading({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <p className={`text-[18px] leading-[1.66] sm:text-[20px] ${className}`}>
      {children}
    </p>
  );
}
